const MOBILE_AUTH_MODE = "mobile";
const DEFAULT_TOKEN_REQUEST_TIMEOUT_MS = 30000;
const TOKEN_EXPIRATION_BUFFER_SECONDS = 30;
const FORCED_REFRESH_COOLDOWN_MS = 5000;

const MESSAGE_TYPE = Object.freeze({
  tokenRequest: "AUTH_TOKEN_REQUEST",
  tokenExpired: "AUTH_TOKEN_EXPIRED",
  tokenResponse: "AUTH_TOKEN_RESPONSE",
  tokenUpdated: "AUTH_TOKEN_UPDATED",
  mobileToken: "MOBILE_AUTH_TOKEN",
  authSession: "AUTH_SESSION",
  authError: "AUTH_ERROR",
  tokenError: "AUTH_TOKEN_ERROR",
  mobileError: "MOBILE_AUTH_ERROR",
  logout: "AUTH_LOGOUT",
});

const TOKEN_SUCCESS_TYPES = new Set([
  MESSAGE_TYPE.tokenResponse,
  MESSAGE_TYPE.tokenUpdated,
  MESSAGE_TYPE.mobileToken,
  MESSAGE_TYPE.authSession,
]);

const TOKEN_ERROR_TYPES = new Set([
  MESSAGE_TYPE.authError,
  MESSAGE_TYPE.tokenError,
  MESSAGE_TYPE.mobileError,
  MESSAGE_TYPE.logout,
]);

const mobileAuthState = {
  idToken: "",
  activeAccount: null,
  pendingTokenRequest: null,
  lastForcedRefreshAt: 0,
};

let restoreFetchInterceptor = null;

export function isMobileAuthMode() {
  const requestedAuthMode = new URLSearchParams(window.location.search).get("authMode");

  return (
    window.__MY_MONTEPIO_AUTH_MODE__ === MOBILE_AUTH_MODE ||
    window.__MY_MONTEPIO_MOBILE_WEBVIEW__ === true ||
    requestedAuthMode === MOBILE_AUTH_MODE
  );
}

export function hasMobileBridge() {
  return typeof window.ReactNativeWebView?.postMessage === "function";
}

export function postMobileAuthMessage(type, payload = {}) {
  if (!hasMobileBridge()) return false;

  const message = JSON.stringify({ type, ...payload });
  window.ReactNativeWebView.postMessage(message);

  return true;
}

export function parseMobileAuthMessage(event) {
  const eventData = event?.data;

  if (!eventData) return null;
  if (typeof eventData === "object") return eventData;
  if (typeof eventData !== "string") return null;

  try {
    return JSON.parse(eventData);
  } catch {
    return { type: eventData };
  }
}

export function addMobileAuthMessageListener(callback) {
  const handleMessage = (event) => {
    const message = parseMobileAuthMessage(event);

    if (message?.type) callback(message);
  };

  window.addEventListener("message", handleMessage);
  document.addEventListener("message", handleMessage);

  return () => {
    window.removeEventListener("message", handleMessage);
    document.removeEventListener("message", handleMessage);
  };
}

export function getTokenFromMobileAuthMessage(message) {
  const payload = message?.payload;

  return (
    message?.idToken ||
    message?.token ||
    message?.accessToken ||
    payload?.idToken ||
    payload?.token ||
    payload?.accessToken ||
    ""
  );
}

export function requestMobileAuthToken({
  forceRefresh = false,
  timeout = DEFAULT_TOKEN_REQUEST_TIMEOUT_MS,
} = {}) {
  if (canReuseCachedToken(forceRefresh)) {
    return Promise.resolve(mobileAuthState.idToken);
  }

  if (mobileAuthState.pendingTokenRequest) {
    return mobileAuthState.pendingTokenRequest;
  }

  return startMobileTokenRequest({ forceRefresh, timeout });
}

export function createMobileAccountFromToken(idToken) {
  const claims = parseJwt(idToken) || {};
  const accountId = claims.sub || "mobile-account";

  return {
    homeAccountId: accountId,
    localAccountId: accountId,
    username: claims.emails?.[0] || claims.email || claims.name || "mobile-user",
    name: claims.name || "",
    clientID: claims.extension_clientId || "",
    idTokenClaims: claims,
  };
}

export function createMobileMsalInstance(idToken = "") {
  if (idToken) activateToken(idToken);

  return {
    getAllAccounts() {
      if (!mobileAuthState.idToken) return [];

      return [getOrCreateActiveAccount()];
    },

    getActiveAccount() {
      return mobileAuthState.activeAccount;
    },

    setActiveAccount(account) {
      mobileAuthState.activeAccount = account;
    },

    async acquireTokenSilent(request = {}) {
      const token = await requestMobileAuthToken({
        forceRefresh: request?.forceRefresh === true,
      });
      const account = activateToken(token);

      return { idToken: token, account };
    },

    async loginRedirect() {
      const token = await requestMobileAuthToken({ forceRefresh: true });
      activateToken(token);
    },

    logoutRedirect() {
      clearMobileSession();
      postMobileAuthMessage(MESSAGE_TYPE.logout);

      return Promise.resolve();
    },

    removeAccount() {
      clearMobileSession();

      return Promise.resolve();
    },
  };
}

export function installMobileAuthFetchInterceptor(onTokenChanged) {
  if (restoreFetchInterceptor) return restoreFetchInterceptor;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const retryInput = cloneRequestForRetry(input);
    const response = await originalFetch(input, init);

    if (response.status !== 401) return response;

    try {
      const token = await requestMobileAuthToken({ forceRefresh: true });
      const retryResponse = await originalFetch(...buildRetryFetchArgs(retryInput, init, token));

      if (retryResponse.status !== 401 && typeof onTokenChanged === "function") {
        onTokenChanged(token);
      }

      return retryResponse;
    } catch {
      return response;
    }
  };

  restoreFetchInterceptor = () => {
    window.fetch = originalFetch;
    restoreFetchInterceptor = null;
  };

  return restoreFetchInterceptor;
}

function startMobileTokenRequest({ forceRefresh, timeout }) {
  let resolveRequest;
  let rejectRequest;

  const request = new Promise((resolve, reject) => {
    resolveRequest = resolve;
    rejectRequest = reject;
  });

  mobileAuthState.pendingTokenRequest = request;

  let hasSettled = false;
  let timeoutId;
  let removeMessageListener = () => {};

  const settleRequest = (settle, value) => {
    if (hasSettled) return;

    hasSettled = true;
    clearTimeout(timeoutId);
    removeMessageListener();

    if (mobileAuthState.pendingTokenRequest === request) {
      mobileAuthState.pendingTokenRequest = null;
    }

    settle(value);
  };

  const rejectWithMessage = (message) => {
    settleRequest(rejectRequest, new Error(message));
  };

  removeMessageListener = addMobileAuthMessageListener((message) => {
    const token = getTokenFromMobileAuthMessage(message);

    if (TOKEN_SUCCESS_TYPES.has(message.type) && token) {
      cacheToken(token, { forcedRefresh: forceRefresh });
      settleRequest(resolveRequest, token);
      return;
    }

    if (TOKEN_ERROR_TYPES.has(message.type)) {
      rejectWithMessage(message.message || message.error || "Erro de autenticacao mobile");
    }
  });

  timeoutId = setTimeout(() => {
    rejectWithMessage("Timeout ao aguardar token da app mobile");
  }, timeout);

  const requestType = forceRefresh ? MESSAGE_TYPE.tokenExpired : MESSAGE_TYPE.tokenRequest;

  if (!postMobileAuthMessage(requestType)) {
    rejectWithMessage("Bridge mobile indisponivel");
  }

  return request;
}

function canReuseCachedToken(forceRefresh) {
  if (!mobileAuthState.idToken) return false;
  if (isJwtExpired(mobileAuthState.idToken, TOKEN_EXPIRATION_BUFFER_SECONDS)) return false;
  if (!forceRefresh) return true;

  return Date.now() - mobileAuthState.lastForcedRefreshAt < FORCED_REFRESH_COOLDOWN_MS;
}

function cacheToken(idToken, { forcedRefresh = false } = {}) {
  mobileAuthState.idToken = idToken;

  if (forcedRefresh) {
    mobileAuthState.lastForcedRefreshAt = Date.now();
  }
}

function activateToken(idToken) {
  cacheToken(idToken);
  mobileAuthState.activeAccount = createMobileAccountFromToken(idToken);

  return mobileAuthState.activeAccount;
}

function getOrCreateActiveAccount() {
  if (!mobileAuthState.activeAccount) {
    mobileAuthState.activeAccount = createMobileAccountFromToken(mobileAuthState.idToken);
  }

  return mobileAuthState.activeAccount;
}

function clearMobileSession() {
  mobileAuthState.idToken = "";
  mobileAuthState.activeAccount = null;
  mobileAuthState.lastForcedRefreshAt = 0;
}

function buildRetryFetchArgs(input, init, token) {
  const requestHeaders = isRequest(input) ? input.headers : undefined;
  const headers = withAuthorizationHeader(init?.headers ?? requestHeaders, token);

  return [input, { ...init, headers }];
}

function cloneRequestForRetry(input) {
  if (!isRequest(input)) return input;

  try {
    return input.clone();
  } catch {
    return input;
  }
}

function isRequest(input) {
  return typeof Request !== "undefined" && input instanceof Request;
}

function withAuthorizationHeader(headers, token) {
  const nextHeaders = new Headers(headers || {});
  nextHeaders.set("Authorization", `Bearer ${token}`);

  return nextHeaders;
}

function parseJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");

    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function isJwtExpired(token, bufferSeconds = 0) {
  const claims = parseJwt(token);

  if (!claims?.exp) return true;

  return claims.exp < Date.now() / 1000 + bufferSeconds;
}
